///
/// EarthBrowser Admin Dashboard - User API
///
/// Created: 2025-11-27
/// Copyright (c) 2025 Geodata Labs
///

import { authenticatedApi } from "./authenticatedApi"
import { setUserProfile, setPermissions, clearUser, type UserProfile } from "../userSlice"
import type {
  PasskeyAuthenticationOptions,
  PasskeyAuthenticationRequest,
} from "@shared-types"

interface ProfileResponse {
  id: number
  email: string
  first_name: string
  last_name: string
  avatar_url?: string
}

interface PermissionsResponse {
  permissions: Array<{
    permission_name: string
    resource: string
    action: string
  }>
  roles: Array<{
    id: number
    name: string
    priority: number
    granted_at: string
    expires_at: string | null
  }>
}

export const userApi = authenticatedApi.injectEndpoints({
  endpoints: (builder) => ({
    getProfile: builder.query<ProfileResponse, void>({
      query: () => "/api/user/profile",
      providesTags: ["UserProfile"],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          const profile: UserProfile = {
            id: data.id,
            email: data.email,
            first_name: data.first_name,
            last_name: data.last_name,
            avatar_url: data.avatar_url,
          }
          dispatch(setUserProfile(profile))
        } catch {
          // Silently ignore - user will need to login
        }
      },
    }),

    getMyPermissions: builder.query<PermissionsResponse, void>({
      query: () => "/api/user/permissions",
      providesTags: ["UserProfile"],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          const permissionNames = data.permissions.map(p => p.permission_name)
          dispatch(setPermissions(permissionNames))
        } catch {
          // Silently ignore
        }
      },
    }),

    signOut: builder.mutation<void, void>({
      query: () => ({
        url: "/api/auth/signout",
        method: "POST",
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          dispatch(clearUser())
        } catch {
          // Still clear user on error
          dispatch(clearUser())
        }
      },
    }),

    // Passkey authentication
    getPasskeyAuthOptions: builder.query<PasskeyAuthenticationOptions, void>({
      query: () => "/api/user/passkey/authentication-options",
    }),

    authenticateWithPasskey: builder.mutation<ProfileResponse, PasskeyAuthenticationRequest>({
      query: (request) => ({
        url: "/api/user/passkey/authenticate",
        method: "POST",
        body: request,
      }),
      invalidatesTags: ["UserProfile"],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          const profile: UserProfile = {
            id: data.id,
            email: data.email,
            first_name: data.first_name,
            last_name: data.last_name,
            avatar_url: data.avatar_url,
          }
          dispatch(setUserProfile(profile))
        } catch {
          // Handle error if needed
        }
      },
    }),
  }),
})

export const {
  useGetProfileQuery,
  useGetMyPermissionsQuery,
  useSignOutMutation,
  useLazyGetPasskeyAuthOptionsQuery,
  useAuthenticateWithPasskeyMutation,
} = userApi

